import dayjs from "dayjs";

/**
 * School and district admins can always copy. A teacher can copy their own breaks
 * only when their profile allows breaks: `me.set_breaks` or `me.modify_schedule`
 * (the same flags as Add break / delete break). Parents never can.
 * District admins use the school routes, so route level stays "school" while
 * jwtData.authentication_type stays "district". Either signal is enough.
 */
export const COPY_BREAKS_TO_TEAMS_ROLES = ["school", "district"] as const;

export const COPY_BREAKS_FALLBACK_ERROR = "Couldn't copy breaks. Please try again.";

const SKIP_REASON_TEXT: Record<string, string> = {
  appointment: "has an appointment",
  break: "already a break",
  team_not_scheduled: "team not scheduled that day",
  not_on_team_schedule: "outside the team's schedule"
};

export type TeacherTeam = {
  uid: string;
  team_name: string;
};

export type SkippedBreak = {
  team_uid: string;
  team_name: string;
  conference_days_id: string | number;
  timeslot: string;
  reason: string;
};

export type CopyBreaksResult = {
  copied: number;
  skipped: number;
  skipped_details?: SkippedBreak[];
};

export type CopyBreaksRequest = {
  teacher_uid: string;
  conference_days_ids: string[];
  team_uids?: string[];
};

export type TeamCalendarSnapshot = {
  team_id: number;
  loading: boolean;
  agenda: { conference_days_id?: string | number }[];
  loadAgenda: (conferenceDayId: string) => unknown;
};

export function canCopyBreaksToTeams(auth: {
  level?: string | null;
  authentication_type?: string | null;
  set_breaks?: boolean | null;
  modify_schedule?: boolean | null;
}): boolean {
  const allowed = COPY_BREAKS_TO_TEAMS_ROLES as readonly string[];
  if (allowed.includes(auth.level || "") || allowed.includes(auth.authentication_type || "")) {
    return true;
  }
  const isTeacher = auth.level === "teacher" || auth.authentication_type === "teacher";
  return isTeacher && !!(auth.set_breaks || auth.modify_schedule);
}

/** Omit team_uids when every team is selected. The API treats that as all of the teacher's teams. */
export function buildCopyBreaksRequest(input: {
  teacherUid: string;
  conferenceDaysIds: string[];
  selectedTeamUids: string[];
  allTeamUids: string[];
}): CopyBreaksRequest {
  const body: CopyBreaksRequest = {
    teacher_uid: input.teacherUid,
    conference_days_ids: input.conferenceDaysIds
  };
  const allSelected =
    input.allTeamUids.length > 0 &&
    input.allTeamUids.every((uid) => input.selectedTeamUids.includes(uid));
  if (!allSelected) body.team_uids = input.selectedTeamUids;
  return body;
}

/**
 * fetchData returns the JSON body for 200 and for 400/403/404/417.
 * It returns false for 401 (the body is discarded) and undefined on a network failure.
 */
export function copyBreaksErrorMessage(body: unknown): string {
  if (!body || typeof body !== "object") return COPY_BREAKS_FALLBACK_ERROR;
  const record = body as { message?: unknown; error?: unknown };
  if (typeof record.message === "string" && record.message.trim()) return record.message;
  if (typeof record.error === "string" && record.error.trim()) return record.error;
  if (record.error && typeof record.error === "object") {
    const messages = Object.values(record.error as Record<string, unknown>).flatMap((value) => {
      if (Array.isArray(value)) return value.map(String);
      if (typeof value === "string") return [value];
      return [];
    });
    if (messages.length) return messages.join(" ");
  }
  return COPY_BREAKS_FALLBACK_ERROR;
}

export function isCopyBreaksResult(body: unknown): body is CopyBreaksResult {
  if (!body || typeof body !== "object") return false;
  const record = body as { copied?: unknown; skipped?: unknown };
  return typeof record.copied === "number" && typeof record.skipped === "number";
}

export function parseTeacherTeams(body: unknown): { teams: TeacherTeam[]; error: string } {
  if (Array.isArray(body)) return { teams: body as TeacherTeam[], error: "" };
  return { teams: [], error: copyBreaksErrorMessage(body) };
}

export function skipReasonText(reason: string): string {
  return SKIP_REASON_TEXT[reason] || reason;
}

export function formatBreakTime(timeslot: string): string {
  const parsed = dayjs(timeslot);
  if (!parsed.isValid()) return timeslot;
  return parsed.format("h:mm A");
}

export function copyBreaksSummary(copied: number, skipped: number): string {
  const breaks = copied === 1 ? "break" : "breaks";
  return `${copied} ${breaks} copied, ${skipped} skipped`;
}

export function groupSkippedByTeam(details: SkippedBreak[] | undefined): {
  team_uid: string;
  team_name: string;
  items: { timeslot: string; reason: string }[];
}[] {
  const groups: {
    team_uid: string;
    team_name: string;
    items: { timeslot: string; reason: string }[];
  }[] = [];
  for (const detail of details || []) {
    let group = groups.find((item) => item.team_uid === detail.team_uid);
    if (!group) {
      group = { team_uid: detail.team_uid, team_name: detail.team_name, items: [] };
      groups.push(group);
    }
    group.items.push({
      timeslot: formatBreakTime(detail.timeslot),
      reason: skipReasonText(detail.reason)
    });
  }
  return groups;
}

/** Reload a team calendar only when that day's slots are already in the agenda store. */
export function refreshTeamCalendarIfLoaded(
  calendar: TeamCalendarSnapshot,
  conferenceDaysIds: string[]
) {
  if (!calendar.team_id || calendar.loading) return;
  const dayId = conferenceDaysIds.find((id) =>
    calendar.agenda.some((row) => String(row.conference_days_id) === String(id))
  );
  if (dayId) calendar.loadAgenda(dayId);
}
